/**
 * Authentication Context
 *
 * Provides global authentication state that can be accessed and updated
 * from any component in the app.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  isAuthenticated as checkIsAuthenticated,
  getToken,
  clearAllTokens,
} from '../services/tokenStorage';
import { installAuthExpirationGuard, setUnauthorizedHandler } from '../services/apiClient';
import { showInfo } from '../utils/toast';

interface AuthContextType {
  isAuthenticated: boolean;
  isInitialized: boolean;
  userId: number | null;
  setIsAuthenticated: (value: boolean) => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to decode JWT and get user ID
function getUserIdFromToken(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.sub ? parseInt(decoded.sub, 10) : null;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);

  const refreshAuth = async () => {
    const isAuth = await checkIsAuthenticated();
    setIsAuthenticated(isAuth);
    setIsInitialized(true);

    // Extract user ID from token
    if (isAuth) {
      const token = await getToken();
      if (token) {
        const id = getUserIdFromToken(token);
        setUserId(id);
      }
    } else {
      setUserId(null);
    }
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  // Redirect to Login when an authenticated API call comes back 401 (expired
  // or revoked JWT), instead of leaving the user staring at an error message.
  // Login/register send no Authorization header, so a bad-credentials 401 on
  // the login screen is unaffected.
  useEffect(() => {
    setUnauthorizedHandler(async () => {
      await clearAllTokens();
      setIsAuthenticated(false);
      setUserId(null);
      showInfo('Your session has expired. Please log in again.');
    });
    installAuthExpirationGuard();
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isInitialized, userId, setIsAuthenticated, refreshAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
